import React, { useEffect, useState, useRef } from 'react';
import { number } from 'prop-types';
import useMiniGamesStore from 'stores/miniGames';
import useUserStore from 'stores/user';
import { text } from 'polyglot/polyglot';
import Spinner from 'components/Spinner/Spinner';
import api from 'utils/api';
import Cross from 'themes/common/svg/cross.svg';
import { formatCurrency } from 'utils/currency';

import styles from './MiniGamesList.module.scss';

const userSelector = (state) => ({
  access_token: state.user.access_token,
  refresh_token: state.user.refresh_token,
  callback_deposit: state.user.callback_deposit,
  callback_wallet: state.user.callback_wallet,
  apiURL: state.user.apiURL,
  currency: state.user.currency || 'SEK',
});

function MiniGamesList({ miniGameListId }) {
  const [isLoading, setIsLoading] = useState(false);
  const [activeGame, setActiveGame] = useState(null);
  const [externalUrl, setExternalUrl] = useState(null);
  const iframeRef = useRef(null);
  const miniGames = useMiniGamesStore((state) => state.miniGames);
  const setMiniGames = useMiniGamesStore((state) => state.setMiniGames);
  const user = useUserStore(userSelector);

  const closeGame = () => {
    setActiveGame(null);
    setExternalUrl(null);
  };

  useEffect(() => {
    const shouldFetch = miniGameListId && miniGames.length === 0;

    const fetchMiniGames = async () => {
      if (!shouldFetch) return;

      try {
        setIsLoading(true);
        const response = await api.get(`/api/MiniGames/${miniGameListId}`);
        setMiniGames(response);
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to fetch mini games:', error);
        setIsLoading(false);
      }
    };

    fetchMiniGames();
  }, [miniGameListId, miniGames.length, setMiniGames]);

  useEffect(() => {
    if (activeGame && activeGame.type === 'External') {
      setIsLoading(true);
      setExternalUrl(null);
      api
        .get(`/api/MiniGames/launch?id=${activeGame.id}`)
        .then((url) => {
          setExternalUrl(url);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Failed to fetch external launch URL:', error);
          setIsLoading(false);
        });
    }
  }, [activeGame]);

  useEffect(() => {
    const handleIframeMessage = (event) => {
      if (
        iframeRef.current &&
        event.source === iframeRef.current.contentWindow
      ) {
        if (event.data && event.data.name) {
          switch (event.data.name) {
            case 'closed':
              closeGame();
              break;
            case 'deposit':
              if (
                user.callback_deposit &&
                typeof user.callback_deposit === 'function'
              ) {
                user.callback_deposit();
              }
              break;
            case 'wallet':
              if (
                user.callback_wallet &&
                typeof user.callback_wallet === 'function'
              ) {
                user.callback_wallet();
              }
              break;
            default:
              break;
          }
        }
      }
    };

    window.addEventListener('message', handleIframeMessage);

    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [user]);

  const handleGameClick = (game) => {
    setIsLoading(true);
    setActiveGame(game);
    if (game.type !== 'External') {
      setTimeout(() => setIsLoading(false), 300);
    }
  };

  const getLauncherUrl = (game) => {
    const params = new URLSearchParams({
      gameId: game.clientName,
      clientId: game.externalId,
      token: user.access_token || '',
      apiURL: user.apiURL || '',
      refreshToken: user.refresh_token || '',
      currencyCode: user.currency,
      language: text.locale(),
    });
    return `./miniGameLauncher.html?${params.toString()}`;
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div className={styles.wrapper}>
      {activeGame && (
        <div className={styles.gameWrapper}>
          <div
            className={styles.gameContainer}
            style={{
              paddingTop: `${(1 / activeGame.aspectRatio) * 100}%`,
            }}
          >
            <iframe
              ref={iframeRef}
              src={
                activeGame.type === 'External' && externalUrl
                  ? externalUrl
                  : getLauncherUrl(activeGame)
              }
              title={activeGame.clientName}
              className={styles.gameFrame}
              allowFullScreen
            />
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={closeGame}
          >
            <span>
              {text.t('miniGames.closeButton')}
              <Cross className={styles.closeIcon} />
            </span>
          </button>
        </div>
      )}
      <div className={styles.container}>
        {!activeGame && (
          <h3 className={styles.heading}>{text.t('miniGames.title')}</h3>
        )}
        {miniGames.length === 0 ? (
          <p className={styles.noGames}>{text.t('miniGames.noMiniGames')}</p>
        ) : (
          <div className={styles.gameGrid}>
            {miniGames.map((game) => (
              <button
                key={game.id}
                type="button"
                className={styles.gameCard}
                onClick={() => handleGameClick(game)}
              >
                <div className={styles.imageContainer}>
                  {game.listImageRaw ? (
                    <img
                      src={`data:image/png;base64,${game.listImageRaw}`}
                      alt={game.clientName}
                      className={styles.thumbnail}
                    />
                  ) : (
                    <div className={styles.placeholderImage}>
                      {game.clientName.charAt(0).toUpperCase()}
                    </div>
                  )}

                  {game.tags && game.tags.length > 0 && (
                    <div className={styles.tagContainer}>
                      {game.tags.map((tag) => (
                        <span key={tag} className={styles.tag}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  {game.wagers && game.wagers.length > 0 && (
                    <div className={styles.wagesContainer}>
                      <span className={styles.wager}>
                        {formatCurrency(
                          Math.min(
                            ...game.wagers.map((wager) => Number(wager))
                          ),
                          0
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

MiniGamesList.propTypes = {
  miniGameListId: number,
};

MiniGamesList.defaultProps = {
  miniGameListId: null,
};

export default MiniGamesList;
